import React, { useEffect, useMemo, useRef } from 'react';
import { PlayIcon } from '@heroicons/react/24/solid';

const TranscriptViewer = ({
  transcript = [],
  highlightText,
  highlightedSegmentIds = [],
  onPlaySegment,
  currentTime
}) => {
  const containerRef = useRef(null);
  const highlightTextRef = useRef(null);
  const segmentRefs = useRef({});

  // Debug: Log the first segment to check data structure
  useEffect(() => {
    if (transcript && transcript.length > 0) {
      // console.log('Transcript Segment 0:', transcript[0]);
    }
  }, [transcript]);

  const normalizedHighlight = useMemo(() => highlightText?.toLowerCase().trim() || '', [
    highlightText,
  ]);
  const highlightedSet = useMemo(() => new Set(highlightedSegmentIds || []), [
    highlightedSegmentIds,
  ]);

  useEffect(() => {
    highlightTextRef.current = null;
  }, [normalizedHighlight]);

  useEffect(() => {
    segmentRefs.current = {};
  }, [transcript]);

  useEffect(() => {
    if (highlightTextRef.current && normalizedHighlight) {
      highlightTextRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [normalizedHighlight, transcript]);

  useEffect(() => {
    const targetId =
      highlightedSegmentIds && highlightedSegmentIds.length ? highlightedSegmentIds[0] : null;
    if (targetId === null || targetId === undefined) return;
    const el = segmentRefs.current[targetId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedSegmentIds]);

  const formatTime = (seconds) => {
    const num = Number(seconds);
    if (!Number.isFinite(num)) return '';
    const m = Math.floor(num / 60);
    const s = Math.floor(num % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div
      ref={containerRef}
      className="space-y-3 max-h-[70vh] overflow-y-auto bg-white border rounded-lg p-4 shadow-sm"
    >
      {transcript.length === 0 && (
        <p className="text-gray-500">Transcript will appear once available.</p>
      )}
      {transcript.map((segment, index) => {
        const matchesHighlight =
          normalizedHighlight && segment.text?.toLowerCase().includes(normalizedHighlight);
        const isChatHighlighted = highlightedSet.has(index);
        const segmentKey = `${segment.speaker || 'speaker'}-${index}`;
        // STRICT NULL CHECK: 
        // JSON "start": null becomes javascript null. Number(null) is 0. 
        // We must explicitly check for null/undefined to avoid 0.00 timestamps on missing data.
        let rawStart = segment.start !== undefined ? segment.start : segment.start_time;
        let start = (rawStart === null || rawStart === undefined) ? null : Number(rawStart);
        
        if (start !== null && !Number.isFinite(start)) start = null;

        let rawEnd = segment.end !== undefined ? segment.end : segment.end_time;
        let end = (rawEnd === null || rawEnd === undefined) ? null : Number(rawEnd);
        if (end !== null && !Number.isFinite(end)) end = null;

        const isActive =
          currentTime !== undefined &&
          start !== null &&
          end !== null &&
          currentTime >= start &&
          currentTime <= end;

        return (
          <div
            id={`transcript-segment-${index}`}
            key={segmentKey}
            ref={(el) => {
              if (el) {
                segmentRefs.current[index] = el;
                if (matchesHighlight) {
                  highlightTextRef.current = el;
                }
              }
            }}
            className={`p-3 rounded-lg border transition-colors ${
              isActive
                ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300'
                : matchesHighlight || isChatHighlighted
                ? 'bg-yellow-100 border-yellow-500'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-indigo-700 text-sm">
                  {segment.speaker || `Speaker ${index + 1}`}
                </span>
                {start !== null && (
                  <span className="text-xs text-gray-400 font-mono">
                    {formatTime(start)}
                  </span>
                )}
              </div>

              {/* Always render button if start time exists */}
              {start !== null && onPlaySegment && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaySegment(start);
                  }}
                  className={`p-1.5 rounded-full transition-colors flex items-center justify-center cursor-pointer ${
                    isActive
                      ? 'text-blue-600 bg-blue-100'
                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                  title={`Play audio from ${formatTime(start)}`}
                  aria-label="Play segment"
                >
                  <PlayIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{segment.text}</p>
          </div>
        );
      })}
    </div>
  );
};

export default TranscriptViewer;
